import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import {
  Shield, Cpu, Receipt, Microscope, Users, Building2,
  CheckCircle2, ArrowLeft, Phone, Mail, MapPin, Star,
  Sparkles, Clock, FlaskConical, HeartHandshake, Target,
  CalendarCheck, FileBarChart
} from 'lucide-react';
import LandingNavbar from '../../components/LandingNavbar';

const C = {
  primary: '#0F6E56',
  secondary: '#1D9E75',
  accent: '#378ADD',
  lightBg: '#F1EFE8',
  darkText: '#2C2C2A',
  mutedText: '#888780',
};

function Fade({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.55, ease: 'easeOut', delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

const VALUES = [
  {
    icon: Target,
    title: 'دقة لا تقبل المساومة',
    desc: 'نُصمم كل جزء من النظام بحيث تكون نتائج المختبر دقيقة ومتسقة — لأن قرارات طبية مبنية عليها.',
  },
  {
    icon: HeartHandshake,
    title: 'شريك نجاح لا مجرد برنامج',
    desc: 'نقيس نجاحنا بنجاح المختبرات التي تستخدمنا. دعمنا متاح دائمًا وفريقنا يعرف تحدياتك.',
  },
  {
    icon: Sparkles,
    title: 'تطوير مستمر',
    desc: 'نُطلق تحديثات دورية مدفوعة بملاحظات المستخدمين الحقيقيين لا المفترضين.',
  },
  {
    icon: Shield,
    title: 'أمان وامتثال',
    desc: 'بياناتك محمية بأعلى معايير التشفير، ومتوافقون مع متطلبات ZATCA والأنظمة السعودية.',
  },
];

const FEATURES_HIGHLIGHT = [
  { icon: CalendarCheck, label: 'إدارة الحجوزات والزيارات المنزلية' },
  { icon: FlaskConical, label: 'كتالوج تحاليل وباقات مرنة' },
  { icon: FileBarChart, label: 'نتائج وتقارير طبية احترافية' },
  { icon: Cpu, label: 'ربط مع أجهزة التحليل (Analyzer Integration)' },
  { icon: Shield, label: 'مراقبة الجودة بمعايير Westgard' },
  { icon: Receipt, label: 'فوترة إلكترونية ZATCA المرحلة الثانية' },
  { icon: Users, label: 'بوابة المرضى لعرض النتائج أونلاين' },
  { icon: Building2, label: 'إدارة تعدد الفروع مركزيًا' },
];

export default function AboutPage() {
  return (
    <div dir="rtl" className="font-[Cairo] bg-white text-[#2C2C2A] min-h-screen" style={{ lineHeight: 1.7 }}>

      <LandingNavbar />

      {/* ─── Hero ─── */}
      <section
        className="pt-28 pb-20 md:pt-36 md:pb-24 px-4 text-center relative overflow-hidden"
        style={{ background: 'linear-gradient(160deg, #fff 60%, #E1F5EE 100%)' }}
      >
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute -top-24 -left-24 w-80 h-80 rounded-full bg-[#1D9E75]/[0.05]" />
          <div className="absolute -bottom-16 -right-16 w-60 h-60 rounded-full bg-[#378ADD]/[0.04]" />
        </div>
        <div className="relative max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 bg-[#E1F5EE] border border-[#1D9E75]/15 rounded-full px-4 py-1.5 mb-6">
              <Microscope className="w-4 h-4 text-[#0F6E56]" />
              <span className="text-sm font-semibold text-[#0F6E56]">من نحن</span>
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold mb-5 leading-[1.4] text-[#2C2C2A]">
              نُبنى خصيصًا{' '}
              <span style={{ color: C.primary }}>للمختبرات السعودية</span>
            </h1>
            <p className="text-[#888780] text-base md:text-lg leading-[1.95] max-w-2xl mx-auto">
              LIS Techs منصة سحابية متكاملة لإدارة المختبرات الطبية، صُممت من الصفر لتلبية احتياجات بيئة العمل في المملكة العربية السعودية — من الفوترة الإلكترونية وفق متطلبات ZATCA حتى ربط أجهزة التحليل ومراقبة الجودة.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ─── القصة ─── */}
      <section className="py-16 md:py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-center">
            <Fade>
              <p className="text-sm font-semibold mb-3" style={{ color: C.secondary }}>قصتنا</p>
              <h2 className="text-2xl sm:text-3xl font-extrabold mb-5 leading-[1.5] text-[#2C2C2A]">
                لماذا بنينا{' '}
                <span style={{ color: C.primary }}>LIS Techs؟</span>
              </h2>
              <div className="space-y-4 text-[#888780] text-sm md:text-base leading-[1.95]">
                <p>
                  لاحظنا أن كثيرًا من المختبرات الطبية في السعودية تعمل على أنظمة قديمة أو أوراق يدوية، مما يُضيّع وقت الموظفين ويزيد احتمالية الخطأ.
                </p>
                <p>
                  في الوقت نفسه، الأنظمة المتاحة إما مُكلفة جدًا، أو مستوردة ولا تتوافق مع المتطلبات المحلية كـ ZATCA وبروتوكولات هيئة الصحة.
                </p>
                <p>
                  فبنينا LIS Techs — نظامًا سحابيًا حديثًا، سهل الاستخدام، بأسعار مناسبة، ومتوافق مع كل المتطلبات التنظيمية السعودية.
                </p>
              </div>
            </Fade>

            <Fade delay={0.1}>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { num: '٩+', label: 'سنوات خبرة في قطاع المختبرات' },
                  { num: '١٠٠٪', label: 'متوافق مع ZATCA المرحلة الثانية' },
                  { num: '٢٤/٧', label: 'دعم فني متاح دائمًا' },
                  { num: 'SaaS', label: 'سحابي بالكامل — لا تثبيت مطلوب' },
                ].map((item, i) => (
                  <div
                    key={i}
                    className="bg-[#F7F7F5] rounded-2xl p-5 border border-gray-100"
                  >
                    <div className="text-2xl font-extrabold mb-1" style={{ color: C.primary }}>
                      {item.num}
                    </div>
                    <div className="text-xs text-[#888780] leading-[1.7]">{item.label}</div>
                  </div>
                ))}
              </div>
            </Fade>
          </div>
        </div>
      </section>

      {/* ─── ماذا يقدم النظام ─── */}
      <section className="py-16 md:py-20 px-4 bg-[#F7F7F5]">
        <div className="max-w-5xl mx-auto">
          <Fade className="text-center mb-12">
            <p className="text-sm font-semibold mb-3" style={{ color: C.secondary }}>ما نقدمه</p>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-[#2C2C2A] leading-[1.5]">
              نظام شامل{' '}
              <span style={{ color: C.primary }}>لكل عمليات مختبرك</span>
            </h2>
          </Fade>
          <div className="grid sm:grid-cols-2 gap-3">
            {FEATURES_HIGHLIGHT.map((f, i) => (
              <Fade key={i} delay={i * 0.05}>
                <div className="flex items-center gap-3 bg-white rounded-xl px-4 py-3.5 border border-gray-100 hover:border-[#1D9E75]/20 transition-colors">
                  <div className="w-9 h-9 rounded-lg bg-[#E1F5EE] flex items-center justify-center flex-shrink-0">
                    <f.icon className="w-4 h-4 text-[#0F6E56]" />
                  </div>
                  <span className="text-sm font-medium text-[#2C2C2A] leading-[1.7]">{f.label}</span>
                  <CheckCircle2 className="w-4 h-4 text-[#1D9E75] mr-auto flex-shrink-0" />
                </div>
              </Fade>
            ))}
          </div>
        </div>
      </section>

      {/* ─── قيمنا ─── */}
      <section className="py-16 md:py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <Fade className="text-center mb-12">
            <p className="text-sm font-semibold mb-3" style={{ color: C.secondary }}>قيمنا</p>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-[#2C2C2A] leading-[1.5]">
              ما يجعلنا{' '}
              <span style={{ color: C.primary }}>مختلفين</span>
            </h2>
          </Fade>
          <div className="grid sm:grid-cols-2 gap-5">
            {VALUES.map((v, i) => (
              <Fade key={i} delay={i * 0.08}>
                <div className="bg-[#F7F7F5] rounded-2xl p-6 border border-gray-100 hover:border-[#1D9E75]/20 hover:shadow-md transition-all">
                  <div className="w-11 h-11 rounded-xl bg-[#E1F5EE] flex items-center justify-center mb-4">
                    <v.icon className="w-5 h-5 text-[#0F6E56]" />
                  </div>
                  <h3 className="font-bold text-[#2C2C2A] mb-2 leading-[1.6]">{v.title}</h3>
                  <p className="text-sm text-[#888780] leading-[1.9]">{v.desc}</p>
                </div>
              </Fade>
            ))}
          </div>
        </div>
      </section>

      {/* ─── تواصل معنا ─── */}
      <section className="py-16 md:py-20 px-4 bg-[#F1EFE8]/40">
        <div className="max-w-3xl mx-auto">
          <Fade className="text-center mb-10">
            <p className="text-sm font-semibold mb-3" style={{ color: C.secondary }}>تواصل معنا</p>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-[#2C2C2A] leading-[1.5]">
              نحن هنا{' '}
              <span style={{ color: C.primary }}>للمساعدة</span>
            </h2>
            <p className="text-[#888780] text-sm mt-3 leading-[1.9]">
              لديك سؤال أو تريد عرضًا تجريبيًا؟ تواصل معنا وسنرد عليك خلال ساعة عمل.
            </p>
          </Fade>
          <div className="grid sm:grid-cols-3 gap-4">
            {[
              { icon: Phone, label: 'الهاتف', value: '+966 5X XXX XXXX' },
              { icon: Mail, label: 'البريد الإلكتروني', value: 'hello@listechs.sa' },
              { icon: MapPin, label: 'الموقع', value: 'الرياض، المملكة العربية السعودية' },
            ].map((item, i) => (
              <Fade key={i} delay={i * 0.08}>
                <div className="bg-white rounded-2xl p-5 border border-gray-100 text-center hover:border-[#1D9E75]/20 hover:shadow-md transition-all">
                  <div className="w-10 h-10 rounded-xl bg-[#E1F5EE] flex items-center justify-center mx-auto mb-3">
                    <item.icon className="w-5 h-5 text-[#0F6E56]" />
                  </div>
                  <p className="text-xs text-[#888780] mb-1 leading-[1.7]">{item.label}</p>
                  <p className="text-sm font-semibold text-[#2C2C2A] leading-[1.7]">{item.value}</p>
                </div>
              </Fade>
            ))}
          </div>
        </div>
      </section>

      {/* ─── CTA ─── */}
      <section className="py-16 md:py-20 px-4 text-center" style={{ backgroundColor: C.primary }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55 }}
          className="max-w-xl mx-auto"
        >
          <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-3 leading-[1.5]">
            جاهز لتجربة LIS Techs؟
          </h2>
          <p className="text-white/60 text-sm mb-8 leading-[1.9]">
            ابدأ تجربتك المجانية الآن — لا تحتاج لبطاقة ائتمان.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/register"
              className="inline-flex items-center justify-center gap-2 text-sm font-bold px-8 py-3.5 rounded-xl bg-white hover:bg-white/90 transition-all shadow-lg"
              style={{ color: C.primary }}
            >
              ابدأ مجانًا الآن
              <ArrowLeft className="w-4 h-4" />
            </Link>
            <Link
              to="/pricing"
              className="inline-flex items-center justify-center gap-2 text-sm font-bold text-white border-2 border-white/25 hover:border-white/50 hover:bg-white/10 px-8 py-3.5 rounded-xl transition-all"
            >
              شاهد الباقات
            </Link>
          </div>
        </motion.div>
      </section>

      {/* ─── Footer ─── */}
      <footer className="bg-[#2C2C2A] text-[#888780] py-8 px-4 text-center">
        <div className="flex items-center justify-center gap-2 mb-3">
          <div className="w-7 h-7 rounded-lg bg-[#1D9E75] flex items-center justify-center">
            <Microscope className="w-3.5 h-3.5 text-white" />
          </div>
          <span className="text-base font-extrabold text-white">LIS Techs</span>
        </div>
        <p className="text-xs leading-[1.9]">منصة سحابية لإدارة المختبرات الطبية في السعودية.</p>
        <p className="text-xs mt-1">
          <Link to="/" className="hover:text-[#1D9E75] transition-colors">الرئيسية</Link>
          {' · '}
          <Link to="/pricing" className="hover:text-[#1D9E75] transition-colors">الباقات</Link>
          {' · '}
          <Link to="/register" className="hover:text-[#1D9E75] transition-colors">التسجيل</Link>
        </p>
      </footer>
    </div>
  );
}
